import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useParams, useOutletContext } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Loader2 } from 'lucide-react';

import { execute } from '@/graphql/execute';
import { deviationJobsQuery } from '@/queries/deviation.query';
import { layersAtom } from '@/atoms/layers.atom';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import type { Project } from '@/graphql/graphql';

import { AnalysisRow } from './AnalysisRow';

export function AnalysesPage() {
  const { t } = useTranslation('project');
  const { projectId } = useParams<{ projectId: string }>();
  const allLayers = useAtomValue(layersAtom);

  const { project, projectLoading, href } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();

  usePageMeta({ title: [t('analyses.tabLabel'), project?.name] });

  const { data, isLoading } = useQuery({
    queryKey: ['deviationJobs', projectId],
    queryFn: () => execute(deviationJobsQuery, { projectId: projectId! }),
    enabled: !!projectId,
    refetchInterval: (query) => {
      const jobs = query.state.data?.deviationJobs ?? [];
      const anyRunning = jobs.some(
        (j) => j.status === 'WAITING' || j.status === 'PROCESSING',
      );
      return anyRunning ? 2000 : false;
    },
  });

  const jobs = data?.deviationJobs ?? [];
  const layersById = Object.fromEntries(allLayers.map((l) => [l.id, l]));
  const candidateReferenceLayers = allLayers.filter(
    (l) => l.type === 'VECTOR' || l.type === 'MODEL' || l.type === 'POINTCLOUD',
  );

  return (
    <PageSkeleton
      steps={[
        { label: projectLoading ? t('edit.loading') : project?.name ?? '', href: href ?? '' },
        { label: t('analyses.tabLabel'), href: `${href}/analyses` },
      ]}
    >
      <div className='max-w-screen-md mx-auto w-full px-4 pt-4 flex-1 flex flex-col'>
        <div className='border border-slate-200/70 rounded-md bg-white dark:bg-stone-900 dark:border-stone-800 overflow-hidden'>
          {isLoading ? (
            <div className='flex items-center gap-2 px-4 py-6 text-sm text-slate-600 dark:text-stone-400'>
              <Loader2 className='size-4 animate-spin' />
              {t('common.loading', { defaultValue: 'Loading…' })}
            </div>
          ) : jobs.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-8'>
              <span className='text-slate-500 dark:text-stone-200 text-sm font-medium'>
                {t('analyses.empty')}
              </span>
            </div>
          ) : (
            jobs.map((job) => (
              <AnalysisRow
                key={job.id}
                job={job}
                layersById={layersById}
                candidateReferenceLayers={candidateReferenceLayers}
                projectId={projectId!}
              />
            ))
          )}
        </div>
      </div>
    </PageSkeleton>
  );
}
